import { type ReactElement, useRef } from "react"

import { Button, type ButtonProps } from "../Button/Button"
import {
  type MediaAssetType,
  validExtensions,
} from "../FileUpload/FileUpload.utils"

interface FileUploadButtonProps extends ButtonProps {
  disabled?: boolean
  /**
   * A callback that is called when the user selects files.
   * @returns An array of files that were selected by the user.
   */
  onFilesSelected: (files: File[]) => void
  assetType?: MediaAssetType
  /**
   * A custom element to use as the button for triggering the upload. This is useful if you want to use a
   * different component than the predefined Button component.
   */
  renderButton?: (onClick: ButtonProps["onClick"]) => ReactElement
}

export function FileUploadButton(props: FileUploadButtonProps) {
  const {
    onFilesSelected,
    disabled,
    assetType = "all",
    renderButton,
    ...buttonProps
  } = props

  const hiddenFileInput = useRef<HTMLInputElement>(null)

  const acceptedFiles =
    assetType === "all" ? "*" : [validExtensions.imageAll].toString()

  function onClick() {
    hiddenFileInput.current?.click()
  }

  function onChange(event: React.ChangeEvent<HTMLInputElement>) {
    const filesUploaded = event.target.files
    const newFiles = Array.from(filesUploaded ?? [])
    // Reset value so we can get an onChange() even if we reselect the same file(s)
    event.currentTarget.value = ""
    onFilesSelected(newFiles)
  }

  return (
    <>
      <input
        ref={hiddenFileInput}
        type="file"
        onChange={onChange}
        disabled={disabled}
        accept={acceptedFiles}
        multiple
        style={{ display: "none" }}
      />
      {renderButton ? (
        renderButton(onClick)
      ) : (
        <Button disabled={disabled} {...buttonProps} onClick={onClick} />
      )}
    </>
  )
}
